import { Box, Container, GridLegacy as Grid, Grow, Typography, useTheme } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { GetServerSideProps, GetServerSidePropsContext, NextPage } from 'next';

import { OfferModel, useGetOffersList } from '@/logic/services/offers';
import { QueryClient, dehydrate } from '@tanstack/react-query';
import { handleError, is500 } from '@/logic/shared';

import { AxiosError } from 'axios';
import React from 'react';
import Seo from '@/components/seo/seo';
import { SeoPagesEnum } from '@/constants/seo-pages-enum';
import { findSeoInsidePage } from '@/logic/helpers/find-seo-inside-page';
import getLangHeaders from '@/logic/helpers/get-lang-headers';

import { pxToRem } from '@/theme/typography';
import { useCity } from '@/logic/hooks';
import { useGetPagesSeoMeta } from '@/logic/services/seo';
import { useGetSettings } from '@/logic/services';
import OfferCard from '@/components/offers/offer-card';
import ImageBoxFuture from '@/components/shared/image-box-future';
import { MovieLoaderWrapper } from '@/components/shared/movie-loader-wrapper';
import loadIntlMessages from '@/logic/helpers/loadIntlMessages';

const noOffersIcon = '/icons/offers/no-offers-icon.svg';
const offerEmptyState = '/icons/offers/offer-empty-state.svg';

export const OffersList: NextPage<{
  serverCityId: number;
  path: string;
}> = ({ serverCityId, path }) => {
  const { data: seoDataList } = useGetPagesSeoMeta();
  const seoPage = findSeoInsidePage(seoDataList?.pageMetaData, SeoPagesEnum.offers);

  const theme = useTheme();
  const intl = useIntl();

  const { cityId: clientCityId } = useCity();
  const cityId = clientCityId || serverCityId;
  const { data: pageData, isLoading } = useGetOffersList({
    params: { cityId },
  });

  const { data: allCmsSetting } = useGetSettings();

  return (
    <>
      <Seo
        title={seoPage?.titleTag || intl.formatMessage({ id: 'offers' })}
        canonical={seoPage?.canonicalURL}
        pagePath={path}
        description={seoPage?.descriptionTag || allCmsSetting?.offerDescription}
        hasFollow={seoPage?.hasFollow}
        hasIndex={seoPage?.hasIndex}
      />
      <Container
        component='section'
        maxWidth='muviFixedContainer'
        sx={{
          paddingX: {
            mobile: '16px',
            tablet: '30px',
            mediumDesktop: '90px',
          },
          marginBottom: pxToRem(122),
        }}
      >
        <Typography
          component='h1'
          sx={{
            fontSize: pxToRem(30),
            color: theme.palette.grey[80],
            my: pxToRem(52),
            fontWeight: 700,
            [theme.breakpoints.down('tablet')]: {
              mt: '32px',
              mb: '24px',
            },
          }}
        >
          <FormattedMessage id='offers' />
        </Typography>
        {!isLoading ? (
          pageData?.offers?.length > 0 ? (
            <Box>
              {allCmsSetting?.offerDescription && (
                <Typography
                  component='p'
                  sx={{
                    fontSize: pxToRem(24),
                    color: theme.palette.grey[50],
                    textAlign: 'center',
                    mb: { tablet: pxToRem(44), mobile: '24px' },
                    maxWidth: '1094px',
                    mx: 'auto',
                    lineHeight: pxToRem(36),
                  }}
                >
                  {allCmsSetting.offerDescription}
                </Typography>
              )}
              <Grid
                container
                rowSpacing={{
                  mobile: '16px',
                  tablet: pxToRem(48),
                }}
                columnSpacing={{
                  mobile: '0px',
                  tablet: pxToRem(46),
                }}
              >
                {pageData?.offers.map((offer: OfferModel, index: number) => (
                  <Grow
                    in
                    style={{
                      transformOrigin: '0 0 0',
                    }}
                    {...{ timeout: 1000 + index * 500 }}
                    key={offer.id}
                  >
                    <Grid item mobile={12} tablet={6} laptop={4}>
                      <OfferCard
                        image={offer?.logoUrl || offerEmptyState}
                        imageAlt={offer?.logoUrlAlt}
                        slug={offer.uniqueName}
                        title={offer.title}
                        description={offer.description}
                        time={offer.endAt}
                        bankName={offer.bankName}
                        validationType={offer.validationType}
                      />
                    </Grid>
                  </Grow>
                ))}
              </Grid>
            </Box>
          ) : (
            <Box
              component='div'
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                position: 'relative',
                pt: { tablet: pxToRem(37), mobile: '26px' },
              }}
            >
              <ImageBoxFuture
                img={noOffersIcon}
                objectFit='contain'
                title='no offers'
                maxWidth='100%'
                sx={{
                  mb: { tablet: pxToRem(24), mobile: '24px' },
                  width: { tablet: pxToRem(375), mobile: '205px' },
                  height: { tablet: pxToRem(220), mobile: '120px' },
                }}
              />
              <Typography
                component='p'
                sx={{
                  fontSize: { tablet: pxToRem(20), mobile: '16px' },
                  textAlign: 'center',
                  color: theme.palette.grey[50],
                  maxWidth: { tablet: pxToRem(461), mobile: '461px' },
                  lineHeight: '150%',
                }}
              >
                <FormattedMessage id='offers.no_offers' />
              </Typography>
            </Box>
          )
        ) : (
          <MovieLoaderWrapper
            innerBox={{
              width: '160px',
              height: '160px',
              [theme.breakpoints.down('laptop')]: {
                width: '100px',
                height: '100px',
              },
            }}
            outerBox={{
              width: '100%',
              height: {
                mobile: 'calc(100vh - 56px)',
                tablet: 'calc(100vh - 100px)',
              },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          />
        )}
      </Container>
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async (
  context: GetServerSidePropsContext
) => {
  const headers = {
    ...getLangHeaders(context.locale),
  };
  const { locale, defaultLocale } = context;
  const cityId = Number(context.req.cookies.city);
  const queryClient = new QueryClient();

  try {
    await useGetOffersList.fetchQuery(queryClient, {
      params: { cityId },
      headers,
    });
  } catch (error: unknown) {
    if (is500(error)) {
      return handleError(error as AxiosError);
    }
  }

  try {
    await useGetPagesSeoMeta.fetchQuery(queryClient, {
      headers,
    });
  } catch (error: unknown) {
    if (is500(error)) {
      return handleError(error as AxiosError);
    }
  }

  // offerDescription renders above the offers grid. Fetched client-side it
  // landed after the grid and pushed it ~250px down (CLS 0.23 on mobile).
  // Not fatal: without it the page just renders no description.
  try {
    await useGetSettings.fetchQuery(queryClient, {
      headers,
    });
  } catch {
    // ignore
  }
  return {
    props: {
      dehydratedState: dehydrate(queryClient),
      serverCityId: cityId,
      path: `${context.locale}${context.resolvedUrl}`,
      intlMessages: await loadIntlMessages(locale as string, defaultLocale),
    },
  };
};
export default OffersList;
